import {
  Toolbar,
  ToolbarButton,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowNext16Regular,
  ArrowPrevious16Regular,
  ChevronLeft16Regular,
  ChevronRight16Regular,
} from '@fluentui/react-icons';
import React from 'react';
import { pageWindow } from './pageWindow';

export interface PaginationBarProps {
  page: number;
  pageCount: number;
  isLastPage: boolean;
  onPage: (page: number) => void;
  children?: React.ReactNode;
}

const useStyles = makeStyles({
  bar: {
    marginTop: tokens.spacingVerticalM,
    gap: tokens.spacingHorizontalXS,
  },
  button: {
    minWidth: '32px',
    height: '32px',
    padding: 0,
    borderRadius: tokens.borderRadiusMedium,
    fontWeight: tokens.fontWeightSemibold,
  },
  idle: {
    color: tokens.colorNeutralForeground3,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  current: {
    ...shorthands.border('1px', 'solid', tokens.colorBrandBackground),
  },
  ellipsis: {
    minWidth: '24px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

const PaginationBar: React.FC<PaginationBarProps> = ({
  page,
  pageCount,
  isLastPage,
  onPage,
  children,
}) => {
  const styles = useStyles();
  const isFirstPage = page === 1;

  return (
    <Toolbar aria-label="Pagination" size="small" className={styles.bar}>
      <ToolbarButton
        appearance="subtle"
        aria-label="First page"
        className={mergeClasses(styles.button, styles.idle)}
        icon={<ArrowPrevious16Regular />}
        disabled={isFirstPage}
        onClick={() => onPage(1)}
      />
      <ToolbarButton
        appearance="subtle"
        aria-label="Previous page"
        className={mergeClasses(styles.button, styles.idle)}
        icon={<ChevronLeft16Regular />}
        disabled={isFirstPage}
        onClick={() => onPage(Math.max(1, page - 1))}
      />
      {pageWindow(page, pageCount).map((item, index) =>
        item === 'ellipsis' ? (
          <span key={`ellipsis-${index}`} className={styles.ellipsis}>
            …
          </span>
        ) : (
          <ToolbarButton
            key={item}
            appearance={item === page ? 'primary' : 'subtle'}
            aria-label={`Page ${item}`}
            aria-current={item === page ? 'page' : undefined}
            className={mergeClasses(
              styles.button,
              item === page ? styles.current : styles.idle
            )}
            onClick={() => onPage(item)}
          >
            {item}
          </ToolbarButton>
        )
      )}
      <ToolbarButton
        appearance="subtle"
        aria-label="Next page"
        className={mergeClasses(styles.button, styles.idle)}
        icon={<ChevronRight16Regular />}
        disabled={isLastPage}
        onClick={() => onPage(page + 1)}
      />
      <ToolbarButton
        appearance="subtle"
        aria-label="Last page"
        className={mergeClasses(styles.button, styles.idle)}
        icon={<ArrowNext16Regular />}
        disabled={isLastPage}
        onClick={() => onPage(Math.max(1, pageCount))}
      />
      {children}
    </Toolbar>
  );
};

export default PaginationBar;
